import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Ban, Bell, Briefcase, CheckCheck, CheckCircle2, ClipboardCheck, DollarSign, FileSignature, FileText, LogIn, LogOut, MessageCircle, MessageSquare, PhoneMissed, Settings, Trash2 } from 'lucide-react';
import type { AppNotification } from '@/types';
import { LayerDepthContext, useModalLayer } from '@/lib/layerManager';
import { navigateApp } from '@/lib/navigation';
import { leaveNotificationsPage } from '@/lib/notificationCenter';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  useNotificationActions,
  useNotificationList,
  useNotificationLiveUpdates,
  useOpenNotification,
  useUnreadNotificationCount,
} from '@/hooks/useNotificationCenter';

dayjs.extend(relativeTime);

const TYPE_ICONS: Record<string, typeof Bell> = {
  JobPaid: DollarSign,
  JobPendingApproval: ClipboardCheck,
  TechnicianAssigned: Briefcase,
  NewMessage: MessageCircle,
  NewSms: MessageCircle,
  EstimatePaid: DollarSign,
  EstimateSigned: FileSignature,
  JobCompleted: CheckCircle2,
  JobCanceled: Ban,
  JobDeleted: Trash2,
  JobConvertedToEstimate: FileText,
  TechnicianCheckedIn: LogIn,
  TechnicianCheckedOut: LogOut,
  MissedCall: PhoneMissed,
  InboundSms: MessageSquare,
  TechnicianMessage: MessageCircle,
};

export function NotificationItem({
  notification: n,
  onOpen,
  onMarkRead,
  action,
}: {
  notification: AppNotification;
  onOpen: (n: AppNotification) => void;
  /** Turns the unread dot into a "mark as read" button. */
  onMarkRead?: (n: AppNotification) => void;
  action?: React.ReactNode;
}) {
  const Icon = TYPE_ICONS[n.type] ?? Bell;
  const unread = !n.read_at;
  return (
    <div className={`flex items-start gap-3 px-4 py-3 ${unread ? 'bg-primary-50/60' : ''}`}>
      <button type="button" onClick={() => onOpen(n)} className="flex flex-1 min-w-0 items-start gap-3 text-left">
        <span className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${unread ? 'bg-primary-100 text-primary-600' : 'bg-secondary-100 text-secondary-500'}`}>
          <Icon className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className={`block truncate text-sm ${unread ? 'font-semibold text-secondary-900' : 'font-medium text-secondary-700'}`}>{n.title}</span>
          <span className="block text-sm text-secondary-600 line-clamp-2">{n.body}</span>
          <span className="block text-xs text-secondary-400 mt-0.5">{dayjs(n.created_at).fromNow()}</span>
        </span>
        {unread && !onMarkRead && <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary-500" aria-label="Unread" />}
      </button>
      {unread && onMarkRead && (
        <button
          type="button"
          onClick={() => onMarkRead(n)}
          className="group -mr-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full hover:bg-secondary-200"
          title="Mark as read"
          aria-label="Mark as read"
        >
          <span className="h-2.5 w-2.5 rounded-full bg-primary-500 group-hover:ring-2 group-hover:ring-primary-200" />
        </button>
      )}
      {action}
    </div>
  );
}

const PANEL_WIDTH = 384;
const MAX_ITEMS = 8;

/** Bell + unread badge for the top bar; opens a dropdown with the latest notifications. */
export function NotificationBell() {
  useNotificationLiveUpdates();
  // Mobile skips the dropdown: the bell goes straight to the notifications page.
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<React.CSSProperties>({});
  const buttonRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);
  // On the page already, a second tap toggles it closed like the desktop dropdown.
  const toggleMobilePage = () =>
    window.location.pathname === '/notifications' ? leaveNotificationsPage() : navigateApp('/notifications');
  // ponytail: no back-navigation entry — closing would history.back() and race the navigation an
  // item click starts. Esc / outside click / item click close it instead.
  const layer = useModalLayer({ enabled: open, onClose: close, backNavigation: false });

  const unreadCount = useUnreadNotificationCount();
  // Only while the dropdown is open: a closed bell needs just the count.
  // One extra row tells us whether to show the "more" hint (next_before is set on any full page,
  // so it can't tell exactly-8 from more-than-8).
  const list = useNotificationList({ pageSize: MAX_ITEMS + 1, enabled: open });
  const { markRead, markAllRead } = useNotificationActions();
  const openNotification = useOpenNotification();
  // Notification settings are org policy, which lives in admin-only Settings.
  const { isAdmin } = useCurrentUser();

  const place = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.min(PANEL_WIDTH, window.innerWidth - 16);
    setPosition({ top: rect.bottom + 8, right: Math.max(8, window.innerWidth - rect.right), width });
  }, []);

  useEffect(() => {
    if (!open) return;
    place();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('resize', place);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, place, close]);

  const count = unreadCount.data ?? 0;
  const fetched = list.data?.pages[0]?.items ?? [];
  const items = fetched.slice(0, MAX_ITEMS);
  const hasMore = fetched.length > MAX_ITEMS;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => (isMobile ? toggleMobilePage() : setOpen(o => !o))}
        className="relative p-2 -m-2 text-gray-700 hover:text-gray-900 hover:bg-gray-300 rounded-lg transition-colors duration-200 shrink-0"
        aria-label={count > 0 ? `Notifications, ${count} unread` : 'Notifications'}
      >
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[1.25rem] h-5 px-1 bg-red-500 text-white text-xs font-bold rounded-full ring-2 ring-gray-200 flex items-center justify-center">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && createPortal(
        <LayerDepthContext.Provider value={layer.depth}>
          <div className="fixed inset-0" style={layer.style} onClick={close}>
            <div
              className="fixed flex max-h-[min(32rem,calc(100dvh-6rem))] flex-col overflow-hidden rounded-xl border border-secondary-200 bg-white shadow-xl"
              style={position}
              onClick={e => e.stopPropagation()}
              role="dialog"
              aria-label="Notifications"
            >
              <div className="flex items-center justify-between border-b border-secondary-200 bg-secondary-100 px-4 py-3">
                <h2 className="text-sm font-semibold text-secondary-900">Notifications</h2>
                <div className="flex items-center gap-3">
                  {count > 0 && (
                    <button
                      type="button"
                      onClick={() => markAllRead.mutate()}
                      disabled={markAllRead.isPending}
                      className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50"
                    >
                      <CheckCheck className="h-4 w-4" /> Mark all as read
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => { close(); navigateApp('/settings?tab=notifications'); }}
                      className="-m-1 p-1 rounded text-secondary-500 hover:text-secondary-700 hover:bg-secondary-200"
                      title="Notification settings"
                      aria-label="Notification settings"
                    >
                      <Settings className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto divide-y divide-secondary-100">
                {list.isLoading ? (
                  <p className="px-4 py-8 text-center text-sm text-secondary-500">Loading…</p>
                ) : items.length === 0 ? (
                  <p className="px-4 py-8 text-center text-sm text-secondary-500">You're all caught up.</p>
                ) : (
                  items.map(n => (
                    <NotificationItem
                      key={n.id}
                      notification={n}
                      onOpen={(item) => { close(); openNotification(item); }}
                      onMarkRead={(item) => markRead.mutate(item.id)}
                    />
                  ))
                )}
                {hasMore && (
                  <p className="px-4 py-2 text-center text-xs text-secondary-500">Showing the latest {MAX_ITEMS}. See all for more.</p>
                )}
              </div>

              <button
                type="button"
                onClick={() => { close(); navigateApp('/notifications'); }}
                className="border-t border-secondary-200 bg-secondary-100 px-4 py-3 text-center text-sm font-medium text-primary-600 hover:bg-secondary-200"
              >
                See all
              </button>
            </div>
          </div>
        </LayerDepthContext.Provider>,
        document.body,
      )}
    </>
  );
}
